import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { memo, useCallback } from 'react';
import {
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  SelectChangeEvent,
  Typography,
  useEventCallback,
} from '@mui/material';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiButton } from 'shell/components/shared/button';
import { DateTime } from 'luxon';
import RegisterTypeList from '../shared/register-type-list';
import CodeAnprList from '../shared/code-anpr-list';
import UseModeList, { FlagTyDomain } from '../shared/use-mode-list';
import { DatePicker } from '@mui/lab';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { validationSchema } from './constant';
import PaymentAmountList from '../shared/payment-amount-list';
import { ItemVM } from 'shell/view-models/item';

interface TypePayment {
  stamp?: string;
  secretariat?: string;
  maritalStatus?: string;
  fixed?: string;
  expenses?: string;
  stampCode?: string;
  maritalStatusCode?: string;
  secretariatCode?: string;
  fixedCode?: string;
  expensesCode?: string;
}

export interface ReleaseModeForm {
  code?: string;
  descriptionRelease?: string;
  descriptionPrint?: string;
  register?: string;
  exemption?: string;
  use?: string;
  dateFrom?: DateTime | null;
  dateTo?: DateTime | null;
  typePayment?: TypePayment;
}

interface FormProps {
  onOk: (FormComponent?: ReleaseModeForm) => void;
  initialValues?: ReleaseModeForm;
  onCancel: () => void;
  disabled?: boolean;
  isEdit?: boolean;
  title?: string;
}

const FormComponent = ({
  onOk,
  initialValues,
  onCancel,
  disabled,
  isEdit,
  title,
}: FormProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const releaseModeForm = useFormik<ReleaseModeForm>({
    initialValues: initialValues || {
      code: '',
      descriptionRelease: '',
      descriptionPrint: '',
      register: '',
      exemption: '',
      use: '',
      dateFrom: null,
      dateTo: null,
      typePayment: {
        stamp: '',
        secretariat: '',
        maritalStatus: '',
        fixed: '',
        expenses: '',
        secretariatCode: '',
        stampCode: '',
        maritalStatusCode: '',
        fixedCode: '',
        expensesCode: '',
      },
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const onChangeRegisterTypeList = useEventCallback((event: SelectChangeEvent<string>) => {
    releaseModeForm.setFieldValue('register', event.target.value);
  });

  const onChangeCodeAnprList = useEventCallback((event: SelectChangeEvent<string>) => {
    releaseModeForm.setFieldValue('exemption', event.target.value);
  });

  const onChangeUseModeList = useEventCallback((event: SelectChangeEvent<string>) => {
    releaseModeForm.setFieldValue('use', event.target.value);
  });

  const onChangeFixedList = useEventCallback((item: ItemVM | undefined) => {
    onChangeTypePayment({
      fixed: item?.value,
      fixedCode: item?.id?.toString(),
    });
  });

  const onChangeExpansesList = useEventCallback((item: ItemVM | undefined) => {
    onChangeTypePayment({
      expenses: item?.value,
      expensesCode: item?.id?.toString(),
    });
  });

  const onChangeMaritialList = useEventCallback((item: ItemVM | undefined) => {
    onChangeTypePayment({
      maritalStatus: item?.value,
      maritalStatusCode: item?.id?.toString(),
    });
  });

  const onChangeSecretariatList = useEventCallback((item: ItemVM | undefined) => {
    onChangeTypePayment({
      secretariat: item?.value,
      secretariatCode: item?.id?.toString(),
    });
  });

  const onChangeStampList = useEventCallback((item: ItemVM | undefined) => {
    onChangeTypePayment({
      stamp: item?.value,
      stampCode: item?.id?.toString(),
    });
  });

  const onChangeTypePayment = useEventCallback((obj: Partial<TypePayment>) => {
    releaseModeForm.setFieldValue('typePayment', {
      ...releaseModeForm.values.typePayment,
      ...obj,
    });
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={releaseModeForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice modalità rilascio"
                  label="Codice modalità rilascio *"
                  id="code"
                  name="code"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={releaseModeForm.values.code}
                  onChange={releaseModeForm.handleChange}
                  inputProps={{
                    maxLength: '5',
                  }}
                  disabled={disabled || isEdit}
                  error={releaseModeForm.touched.code && Boolean(releaseModeForm.errors.code)}
                  helperText={releaseModeForm.errors.code}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <PmiTextField
                  aria-label="Descrizione modalità rilascio"
                  label="Descrizione modalità rilascio"
                  id="descriptionRelease"
                  name="descriptionRelease"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={releaseModeForm.values.descriptionRelease}
                  onChange={releaseModeForm.handleChange}
                  inputProps={{
                    maxLength: '50',
                  }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <PmiTextField
                  aria-label="Descrizione modalità stampa"
                  label="Descrizione modalità stampa"
                  id="descriptionPrint"
                  name="descriptionPrint"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={releaseModeForm.values.descriptionPrint}
                  onChange={releaseModeForm.handleChange}
                  inputProps={{
                    maxLength: '240',
                  }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <RegisterTypeList
                  required
                  onChange={onChangeRegisterTypeList}
                  value={releaseModeForm.values.register || ''}
                  disabled={disabled}
                  error={
                    releaseModeForm.touched.register && Boolean(releaseModeForm.errors.register)
                  }
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <CodeAnprList
                  onChange={onChangeCodeAnprList}
                  value={releaseModeForm.values.exemption || ''}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid item xs={12} md={5}>
                  <UseModeList
                    onChange={onChangeUseModeList}
                    value={releaseModeForm.values.use || ''}
                    disabled={disabled}
                    params={{
                      tyDomain: FlagTyDomain.Elettorale,
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} md={5}>
                <DatePicker
                  label="Data inizio validità"
                  aria-label="Data inizio validità"
                  value={releaseModeForm.values.dateFrom || null}
                  onChange={(date) => {
                    releaseModeForm.setFieldValue('dateFrom', date);
                  }}
                  renderInput={(params) => (
                    <PmiTextField
                      id="date-start-validity"
                      {...params}
                      fullWidth
                      error={
                        releaseModeForm.touched.dateFrom && Boolean(releaseModeForm.errors.dateFrom)
                      }
                      helperText={releaseModeForm.errors.dateFrom}
                    />
                  )}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <DatePicker
                  label="Data fine validità"
                  aria-label="Data fine validità"
                  value={releaseModeForm.values.dateTo || null}
                  onChange={(date) => {
                    releaseModeForm.setFieldValue('dateTo', date);
                  }}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="date-end-validity"
                      fullWidth
                      error={
                        releaseModeForm.touched.dateTo && Boolean(releaseModeForm.errors.dateTo)
                      }
                      helperText={releaseModeForm.errors.dateTo}
                    />
                  )}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <PmiTitleDivider>Tipo pagamento</PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <WrappedLabel label="Imposta di bollo">
                  <PaymentAmountList
                    onChange={onChangeStampList}
                    value={releaseModeForm.values.typePayment?.stampCode || ''}
                    disabled={disabled}
                    label="Imposta di bollo"
                    placeholder="Imposta di bollo"
                    id="stamp"
                    name="stamp"
                    type="stampDuty"
                    error={
                      (releaseModeForm.touched.typePayment as unknown as FormikTouched<TypePayment>)
                        ?.stampCode &&
                      Boolean(
                        (releaseModeForm.errors.typePayment as unknown as FormikErrors<TypePayment>)
                          ?.stampCode
                      )
                    }
                  />
                </WrappedLabel>
              </Grid>
              <Grid item xs={12}>
                <WrappedLabel label="Diritti di segreteria">
                  <PaymentAmountList
                    onChange={onChangeSecretariatList}
                    value={releaseModeForm.values.typePayment?.secretariatCode || ''}
                    disabled={disabled}
                    error={
                      (releaseModeForm.touched.typePayment as unknown as FormikTouched<TypePayment>)
                        ?.secretariatCode &&
                      Boolean(
                        (releaseModeForm.errors.typePayment as unknown as FormikErrors<TypePayment>)
                          ?.secretariatCode
                      )
                    }
                    placeholder="Diritti di segreteria"
                    label="Diritti di segreteria"
                    id="secretariat"
                    name="secretariat"
                    type="administrativeFees"
                  />
                </WrappedLabel>
              </Grid>
              <Grid item xs={12}>
                <WrappedLabel label="Diritti di stato civile">
                  <PaymentAmountList
                    onChange={onChangeMaritialList}
                    value={releaseModeForm.values.typePayment?.maritalStatusCode || ''}
                    disabled={disabled}
                    label="Diritti di stato civile"
                    placeholder="Diritti di stato civile"
                    id="maritalStatus"
                    name="maritalStatus"
                    type="civilState"
                    error={
                      (releaseModeForm.touched.typePayment as unknown as FormikTouched<TypePayment>)
                        ?.maritalStatusCode &&
                      Boolean(
                        (releaseModeForm.errors.typePayment as unknown as FormikErrors<TypePayment>)
                          ?.maritalStatusCode
                      )
                    }
                  />
                </WrappedLabel>
              </Grid>
              <Grid item xs={12}>
                <WrappedLabel label="Diritti fissi">
                  <PaymentAmountList
                    onChange={onChangeFixedList}
                    value={releaseModeForm.values.typePayment?.fixedCode || ''}
                    disabled={disabled}
                    label="Diritti fissi"
                    placeholder="Diritti fissi"
                    id="fixed"
                    name="fixed"
                    type="fixedRights"
                    error={
                      (releaseModeForm.touched.typePayment as unknown as FormikTouched<TypePayment>)
                        ?.fixedCode &&
                      Boolean(
                        (releaseModeForm.errors.typePayment as unknown as FormikErrors<TypePayment>)
                          ?.fixedCode
                      )
                    }
                  />
                </WrappedLabel>
              </Grid>
              <Grid item xs={12}>
                <WrappedLabel label="Rimborso spese">
                  <PaymentAmountList
                    onChange={onChangeExpansesList}
                    value={releaseModeForm.values.typePayment?.expensesCode || ''}
                    disabled={disabled}
                    label="Rimborso spese"
                    placeholder="Rimborso spese"
                    id="expenses"
                    name="expenses"
                    type="expenseAccount"
                    error={
                      (releaseModeForm.touched.typePayment as unknown as FormikTouched<TypePayment>)
                        ?.expensesCode &&
                      Boolean(
                        (releaseModeForm.errors.typePayment as unknown as FormikErrors<TypePayment>)
                          ?.expensesCode
                      )
                    }
                  />
                </WrappedLabel>
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

// eslint-disable-next-line react/prop-types
const WrappedLabel: React.FC<{ label: string }> = ({ label, children }) => (
  <Grid container item xs={12}>
    <Grid item xs={12} md={4} display="flex" alignItems="center">
      {label}
    </Grid>
    <Grid item xs={12} md={3}>
      {children}
    </Grid>
  </Grid>
);

WrappedLabel.displayName = 'component with label';

FormComponent.displayName = 'feature auth form component';

const Form = memo(FormComponent);

export default Form;
